import { Search } from '@mui/icons-material'
import { Box, Grid, InputAdornment, TextField, Typography, useTheme } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MUIThemeColors } from 'src/mui-theme'
import { filterFormSchemaDashboardRows } from 'src/screens/customer/form/formSchemaDashboardHelpers'
import type { FormSchemaDashboardRow } from 'src/screens/customer/form/formSchemaDashboardTypes'
import { getThemeColor } from 'src/utils'
import NoFormSchema from '../../assets/svg/no-items-common-image.svg'
import { ICustomerFormSchema } from '../../generated-types/form-schema'
import { EmptyStatusCard } from '../mooring/EmptyStatusCard'
import { FormSchemaPickerCard } from './FormSchemaPickerCard'

export interface FormSchemasDialogPickerPanelProps {
  data: ICustomerFormSchema[] | undefined
  onSelectForm: (form: ICustomerFormSchema) => void
}

export const FormSchemasDialogPickerPanel: React.FC<FormSchemasDialogPickerPanelProps> = ({
  data,
  onSelectForm,
}) => {
  const theme = useTheme()
  const { t } = useTranslation(['form-schema', 'common'])
  const [search, setSearch] = useState('')

  const filteredRows = useMemo(
    () => filterFormSchemaDashboardRows((data ?? []) as FormSchemaDashboardRow[], search),
    [data, search],
  )

  if (!data?.length) {
    return (
      <Box display="flex" flexDirection="row" justifyContent="center">
        <EmptyStatusCard
          imageWidth={'162px'}
          image={NoFormSchema}
          header={t('form-schema-empty-status-title', { ns: 'form-schema' })}
          content={t('form-schema-empty-status-content', { ns: 'form-schema' })}
          headerFontSize="18px"
          contentFontSize="14px"
          contentClasses="mt-2"
          contentTopMargin="10px"
          imageContainerClasses="pb-4 pt-4"
          height="100%"
        />
      </Box>
    )
  }

  return (
    <>
      <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder={t('search-forms-placeholder', { ns: 'form-schema' })}
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: 1, bgcolor: getThemeColor(theme, MUIThemeColors.white) }}
        />
      </Box>

      {!filteredRows.length ? (
        <Box py={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            {t('no-forms-match-filter', { ns: 'form-schema' })}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 'min(58vh, 520px)', overflowY: 'auto', pr: 0.5, pb: 2 }}>
          <Grid container>
            {filteredRows.map(form => (
              <Grid  data-test-id="schema-list-item" item xs={12} sm={6} md={4} lg={3} key={form.id ?? form.name} sx={{ pr: 2, pb: 2 }}>
                <FormSchemaPickerCard schema={form as FormSchemaDashboardRow} onSelect={() => onSelectForm(form)} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  )
}
